import { useWallet } from "@solana/wallet-adapter-react"
import bs58 from "bs58"
import { useCallback } from "react"
import { SignatureError } from "../../lib/errors"
import type { SignFunction } from "../../types"

// Type guard to check if adapter has signMessage method
const hasSignMessage = (
  adapter: unknown,
): adapter is { signMessage: (message: Uint8Array) => Promise<Uint8Array> } => {
  return (
    adapter != null &&
    typeof adapter === "object" &&
    "signMessage" in adapter &&
    typeof adapter.signMessage === "function"
  )
}

export const useSvmSignFunction = () => {
  const { signMessage } = useWallet()

  const sign: SignFunction = useCallback(
    async ({ message, connector }) => {
      // Try to use the connector's adapter directly if available
      const solanaAdapter = connector?.baseConnector
      if (hasSignMessage(solanaAdapter)) {
        try {
          const messageBuffer = new TextEncoder().encode(message)
          const signature = await solanaAdapter.signMessage(messageBuffer)
          return bs58.encode(signature)
        } catch (_error) {
          // Fall through to global context if connector signing fails
        }
      }

      // Fallback to global wallet context
      if (!signMessage) {
        throw new SignatureError(new Error("signMessage is not available"))
      }
      const messageBuffer = new TextEncoder().encode(message)
      const signature = await signMessage(messageBuffer)
      return bs58.encode(signature)
    },
    [signMessage],
  )

  return sign
}
